/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useMemo } from 'react';
import styled from 'styled-components';
import { isNil, isNumber, isUndefined } from 'lodash-es';
import { TableRowProps } from '@mui/material';
import { externalLinkProps } from '../../utils/consts';
import { getPrice } from '../../utils/currency';
import { getPercentages } from '../../utils/percentages';
import { useTruncateText } from '../../hooks';
import {
  PortfolioCurrency,
  ExpectedPortfolioTokenStatus,
  type PortfolioRowResponse,
  type ExpectedPortfolioRowResponse,
} from '../../apollo';
import { usePortfolioCurrency } from '../../providers';
import {
  ChangeCell,
  NameCell,
  OrderCell,
  PercentageCell,
  PriceCell,
  TableRow,
  TokenAmountCell,
  ValueCell,
  NameCellText,
  NameCellDesc,
  TableCell,
} from '../../styled/portfolio';

export type PortfolioTableRowProps = {
  data: PortfolioTableRowData;
  index: number;
  withFiatIndex?: boolean;
  withTickerLink?: boolean;
} & TableRowProps;

const PortfolioTableRow = ({
  data,
  index,
  withFiatIndex = false,
  withTickerLink = true,
  ...props
}: PortfolioTableRowProps) => {
  const truncateText = useTruncateText();
  const { currency } = usePortfolioCurrency();

  const orderCellValue = useMemo(() => {
    if (data?.status === ExpectedPortfolioTokenStatus.Removed) {
      return '-/-';
    }

    return withFiatIndex || !data.isFiat ? `${index + 1}.` : '';
  }, [data.isFiat, data.status, index, withFiatIndex]);

  const {
    nameText,
    price,
    changePercent,
    amountCoins,
    value,
    percentage,
    changePercentValue,
  } = useMemo(() => {
    const nameText = <span>{truncateText(data.title)}</span>;

    const exists = data?.status !== ExpectedPortfolioTokenStatus.Removed;
    const euro = currency === PortfolioCurrency.Eur;
    const getPriceFnCurrency = !euro
      ? {
          currency: 'USD',
        }
      : undefined;

    const priceValue = euro ? data.eurValue : data.usdValue;
    const price = !isUndefined(priceValue)
      ? getPrice(priceValue, getPriceFnCurrency, false)
      : undefined;

    const changePercentValue = euro
      ? data.change24Percentage
      : data.usdChange24Percentage;

    const changePercent = !isUndefined(changePercentValue)
      ? getPercentages(exists ? changePercentValue : 0, {
          withSign: true,
        })
      : undefined;

    const amountCoins = (exists ? data.amountCoins : 0).toLocaleString('nl-NL');

    const value = getPrice(
      exists ? (euro ? data.cost : data.usdCost) : 0,
      getPriceFnCurrency
    );

    const percentage = getPercentages(exists ? data.percentage : 0);

    return {
      nameText,
      price,
      changePercent,
      amountCoins,
      value,
      percentage,
      changePercentValue,
    };
  }, [currency, data, truncateText]);

  const positiveChange = useMemo(() => {
    const exists = data?.status !== ExpectedPortfolioTokenStatus.Removed;
    return exists && isNumber(changePercentValue) && changePercentValue !== 0
      ? changePercentValue > 0
      : undefined;
  }, [data?.status, changePercentValue]);

  const colorInherit = useMemo(
    () =>
      !isNil(data?.status) &&
      data.status !== ExpectedPortfolioTokenStatus.Existing,
    [data.status]
  );

  const hasPrice = !isUndefined(price);
  const hasChangePercent = !isUndefined(changePercent);

  const nameElement = useMemo(() => {
    if (data.isFiat) {
      return <NameCellText>{nameText}</NameCellText>;
    }

    const content = (
      <>
        {nameText}
        <br />
        <NameCellDesc $colorInherit={colorInherit}>
          {data.symbol.toUpperCase()}
        </NameCellDesc>
      </>
    );

    if (withTickerLink) {
      return (
        <NameCellLink as={'a'} href={data.messari} {...externalLinkProps}>
          {content}
        </NameCellLink>
      );
    }

    return <NameCellText>{content}</NameCellText>;
  }, [
    data.isFiat,
    data.messari,
    data.symbol,
    nameText,
    withTickerLink,
    colorInherit,
  ]);

  return (
    <TableRow $status={data?.status} {...props}>
      <OrderCell>{orderCellValue}</OrderCell>
      <NameCell>
        <div>
          <NameCellIconImg src={data.icon || ''} />
          {nameElement}
        </div>
      </NameCell>
      {data.isFiat ? (
        <>
          {hasPrice && <TableCell />}
          {hasChangePercent && <TableCell />}
          <TableCell />
        </>
      ) : (
        <>
          {hasPrice && <PriceCell>{price}</PriceCell>}
          {hasChangePercent && (
            <ChangeCell
              $positiveChange={positiveChange}
              $jadeGreenColor={data.status === ExpectedPortfolioTokenStatus.New}
            >
              {changePercent}
            </ChangeCell>
          )}
          <TokenAmountCell>{amountCoins}</TokenAmountCell>
        </>
      )}
      <ValueCell>{value}</ValueCell>
      <PercentageCell>{percentage}</PercentageCell>
    </TableRow>
  );
};

type PortfolioTableRowBaseData = Pick<
  PortfolioRowResponse['currency'],
  'id' | 'symbol' | 'title' | 'icon' | 'isFiat' | 'messari'
> &
  Pick<PortfolioRowResponse, 'cost' | 'usdCost' | 'percentage' | 'amountCoins'>;

type PortfolioTableRowOptionalPriceData = Partial<
  Pick<PortfolioRowResponse['currency'], 'eurValue' | 'usdValue'>
>;

type PortfolioTableRowOptionalChangeData = Partial<
  Pick<PortfolioRowResponse, 'change24Percentage' | 'usdChange24Percentage'>
>;

type PortfolioTableRowExpectedStatusData = Partial<
  Pick<ExpectedPortfolioRowResponse, 'status'>
>;

export type PortfolioTableRowData = PortfolioTableRowBaseData &
  PortfolioTableRowOptionalPriceData &
  PortfolioTableRowOptionalChangeData &
  PortfolioTableRowExpectedStatusData;

const NameCellIconImg = styled.img`
  width: 22px;
  height: 22px;
  margin-right: 10px;
  object-fit: contain;

  ${({ theme }) => theme.breakpoints.down('xs')} {
    width: 18px;
    height: 18px;
    margin-right: 7px;
  }
`;

const NameCellLink = styled(NameCellText)`
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export { PortfolioTableRow };
